import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import LanguageContext from '../../context/LanguageContext.js';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import './Theme.css';
import Button from '../../components/UI/Button/Button';

const Theme = () => {
    const { t } = useTranslation();
    const { dir } = useContext(LanguageContext);
    const { currentTheme, switchTheme, availableThemes, canSwitch, themeSource } = useTheme();
    const layoutDir = dir();

    const themeIcons = {
        classic: '🌿',
        modern: '💙',
        warm: '🧡',
        watch: '⌚',
        dark: '🌙',
    };

    const handleThemeSelect = (themeName) => {
        if (!canSwitch) return;
        switchTheme(themeName);
    };

    const lockedMessage =
        themeSource === 'preview'
            ? t('theme.lockedPreview', { defaultValue: 'Previewing a theme from the dashboard editor.' })
            : themeSource === 'store'
              ? t('theme.lockedStore', {
                    defaultValue: 'This store uses a published theme. Theme switching is locked.',
                })
              : '';

    return (
        <main className="main">
            <Breadcrumb title={t('theme.title')} />

            <section className="theme-page">
                <div className="container theme-layout" dir={layoutDir}>
                    <div className="theme-panel">
                        <p className="theme-lead">
                            {canSwitch ? t('theme.description') : lockedMessage || t('theme.description')}
                        </p>

                        <div className="theme-sections">
                            <div className="theme-section">
                                <div className="theme-options">
                                    {availableThemes.map((theme) => (
                                        <button
                                            key={theme}
                                            type="button"
                                            className={`theme-option${currentTheme === theme ? ' theme-option--active' : ''}`}
                                            onClick={() => handleThemeSelect(theme)}
                                            aria-pressed={currentTheme === theme}
                                            disabled={!canSwitch}
                                        >
                                            <span className="theme-option-emoji" aria-hidden>
                                                {themeIcons[theme] || '✨'}
                                            </span>
                                            <span className="theme-option-body">
                                                <span className="theme-option-title">
                                                    {t(`theme.themeNames.${theme}`)}
                                                </span>
                                                <span className="theme-option-description">
                                                    {t(`theme.themeDescriptions.${theme}`)}
                                                </span>
                                            </span>
                                            {currentTheme === theme && (
                                                <span className="theme-option-badge">{t('theme.active')}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="theme-preview-sidebar" aria-labelledby="theme-preview-heading">
                        <h2 id="theme-preview-heading" className="theme-preview-sidebar-title">
                            {t('theme.preview')}
                        </h2>
                        <div className="theme-preview-inner">
                            <Button as="div" className="theme-sample-button">
                                {t('theme.sampleButton')}
                            </Button>
                            <div className="theme-sample-text">
                                <h3>{t('theme.sampleHeading')}</h3>
                                <p>{t('theme.sampleText')}</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Theme;
